import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../../Layouts/MainLayout";
import ManageUsersTemplate from "../templates/ManageUsersTemplate";
import { User } from "../types/User";
import { UserDetails } from "../types/User";
import { service } from "../../../services/AxiosServices";
import { toast } from "react-toastify";
import UserModal from "../molecules/UserModal";
import SectionHeader from "../molecules/SectionHeader";
import AccessControl from "../molecules/AccessControl";
import AccessDenied from "../atoms/AccessDenied";
import Spinner from "../atoms/Spinner";
import useAccessCheckLoading from "../../../utils/useAccessCheckLoading";
interface UserRole {
  id: number;
  name: string;
}

interface UserRoleResponse {
  id: number;
  user: {
    id: number;
    username: string;
  };
  role: UserRole[];
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

const ManageUsers: React.FC = () => {
  const [tabValue, setTabValue] = useState<number>(0);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [allUsers, setAllUsers] = useState<UserDetails[]>([]);
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string; id?: number }[]>([]);
  const [roleChanges, setRoleChanges] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const [userRoles, setUserRoles] = useState<Record<number, UserRole[]>>({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
   const [error,setError] = useState('')
   const accessCheckLoading = useAccessCheckLoading()

  const fetchUserRoles = async () => {
    try {
      const response = await service<{ response: UserRoleResponse[] }>("/user-roles/", "get");
      const rolesMap: Record<number, UserRole[]> = {};
      
      response.data.response.forEach((userRole) => {
        rolesMap[userRole.user.id] = userRole.role;
      });
      
      setUserRoles(rolesMap);
    } catch (err) {
      console.error("Error fetching user roles:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // First fetch user roles
      const response = await service<{ response: UserRoleResponse[] }>("/user-roles/", "get");
      const rolesMap: Record<number, UserRole[]> = {};
      response.data.response.forEach((userRole) => {
        rolesMap[userRole.user.id] = userRole.role;
      });
      setUserRoles(rolesMap);
  
      // Then fetch user data
      const userResponse = await service<{ 
        active_user_info: any[],
        inactive_user_info: any[]
      }>("/get-users-by-project/", "post", {
        project_id: localStorage.getItem('project_id')
      });
      
      //  active users
      const activeUsers = (userResponse?.data?.active_user_info || []).map((user) => {
        const userRolesList = rolesMap[user.id] || [];
        const roleNames = userRolesList.map((role) => role.name).join(", ");
        const crewNames = user?.crew_details?.map((crews:any)=>crews.crew_name).join(", ");
        const spaceNames = user?.ai_spaces?.map((knowledgeHub:any)=>knowledgeHub.space_name).join(", ");
  
        return {
          id: user.id,
          username: `${user.first_name || ""}_${user.last_name || ""}`?.toLowerCase(),
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          email: user.email || "",
          role: roleNames,
          status: "active",
          knowledgeHub: spaceNames,
          crews: crewNames,
        };
      });
      
      //  inactive users
      const inactiveUsers = (userResponse?.data?.inactive_user_info || []).map((user) => {
        const userRolesList = rolesMap[user.id] || [];
        const roleNames = userRolesList.map((role) => role.name).join(", ");
        const crewNames = user?.crew_details?.map((crews:any)=>crews.crew_name).join(", ");
        const spaceNames = user?.ai_spaces?.map((knowledgeHub:any)=>knowledgeHub.space_name).join(", ");
  
        return {
          id: user.id,
          username: `${user.first_name || ""}_${user.last_name || ""}`?.toLowerCase(),
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          email: user.email || "",
          role: roleNames,
          status: "inactive",
          knowledgeHub: spaceNames,
          crews: crewNames,
        };
      });
      
      // Combine both arrays
      const allApiUsers = [...activeUsers, ...inactiveUsers];
      
      setAllUsers(allApiUsers);
      updateFilteredUsers(allApiUsers, search, tabValue);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchUserRoles();
      await fetchUsers();
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await service<{ response: any[] }>("/roles", "post", {});

        const apiRoles = response.data.response
          .map((role: any) => ({
            label: role.name,
            value: role.name,
            id: Number(role.id),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setRoleOptions(apiRoles);
      } catch (err) {
        console.error("Error fetching roles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const updateFilteredUsers = (userList: UserDetails[], searchTerm: string, activeTab: number) => {
    const status = activeTab === 0 ? "active" : "inactive";
    const filteredUsers = userList.filter(user => {
      const matchesSearch = !searchTerm || 
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase());
  
      return matchesSearch && user.status === status;
    });
  
    if (filteredUsers.length === 0) {
      setError("No result found");
    } else {
      setError("");
    }
  
    setUsers(filteredUsers);
  };
  
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      updateFilteredUsers(allUsers, search, tabValue);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [search, tabValue, allUsers]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    updateFilteredUsers(allUsers, search, newValue);
  };

  const handleRoleChange = useCallback((username: string, roles: string) => {
    setRoleChanges((prev) => ({ ...prev, [username]: roles }));
  }, []);

  const handleUserStatusChange = async (userId: number, newStatus: string) => {
    try {
   
      const formData = new FormData();
      formData.append('user', userId.toString());
      formData.append('is_active', (newStatus === 'active').toString());
      
      await service<any>('/edit-user', 'post', formData, 'multipart/form-data');
      await fetchUsers()
      toast.success(
        newStatus === 'active' 
          ? 'User activated successfully!' 
          : 'User archived successfully!',
        { autoClose: 2000 }
      );
    } catch (err) {
      console.error("Error updating user status:", err);
      toast.error('Error updating user status');
    }
  };
  const handleSaveAll = async () => {
    const payload = {
      users: users.map((user) => {
  
        const hasChanges = roleChanges.hasOwnProperty(user.username);
        
 
        let roleIds: number[] = [];
        if (hasChanges) {
       
          if (roleChanges[user.username] === "") {
            roleIds = [];
          } else {
            roleIds = roleOptions
              .filter(role => roleChanges[user.username].includes(role.value))
              .map(role => role.id || 0);
          }
        } else {
        
          roleIds = userRoles[user.id]?.map(role => role.id) || [];
        }
        
        return {
          user_id: user.id,
          role_ids: roleIds
        };
      }),
    };
  

    
    try {
      await service("/user-roles/manage/", "post", payload);
      await fetchUserRoles();
      await fetchUsers(); 
      setRoleChanges({}); 
      toast.success("Roles updated successfully", { autoClose: 2000 });
    } catch (err) {
      console.error("Error saving user roles:", err);
      toast.error("Error updating roles");
    }
  };
  const handleSaveNewUser = async (newUser: User) => {
    await fetchUsers();
    setShowAddUserModal(false);
  };
  const handleEditUser = (user: User) => {
    setEditUser(user);
    setShowAddUserModal(true);
  };
  


  const handleAddUser = () => setShowAddUserModal(true);

const handleCloseModal = () => {
  setEditUser(null);
  setShowAddUserModal(false);
};

  return (
    <MainLayout>
      <AccessControl
        requiredPermissions={[
          {
            permission: "manage_users_roles",
            types: ["read"],
          },
        ]}
        renderNoAccess={ accessCheckLoading ? <Spinner/> :<AccessDenied />}
      >
      
      <SectionHeader back={true} title="Manage users" onAdd={handleAddUser} action='Create user' />
      <ManageUsersTemplate
        tabValue={tabValue}
        search={search}
        users={users}
        setUsers={setUsers}
        roleOptions={roleOptions}
        onTabChange={handleTabChange}
        onEdit={handleEditUser} 
        onSearchChange={handleSearchChange}
        onRoleChange={handleRoleChange}
        onSaveAll={handleSaveAll}
        clientErrors={clientErrors}
        onUserStatusChange={handleUserStatusChange}
        loading={loading}
        error={error}
      />
      <UserModal 
        show={showAddUserModal} 
        onClose={handleCloseModal} 
        onSave={handleSaveNewUser} 
        editUser={editUser}
      />
      </AccessControl>
    </MainLayout>
  );
};

export default ManageUsers;